var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Ciphertext, { AESGCMNonceSize, KDFSaltSize } from './Ciphertext';
// crypto should provide access to standard Web Crypto API
// in both the browser environment and node.
export const crypto = typeof window !== 'undefined'
    ? window.crypto
    : // eslint-disable-next-line @typescript-eslint/no-var-requires
        require('crypto').webcrypto;
const hkdfNoInfo = new ArrayBuffer(0);
// This is a variation of https://github.com/paulmillr/noble-secp256k1/blob/main/index.ts#L1378-L1388
// that uses `digest('SHA-256', bytes)` instead of `digest('SHA-256', bytes.buffer)`
// which seems to produce different results.
export function sha256(bytes) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Uint8Array(yield crypto.subtle.digest('SHA-256', bytes));
    });
}
// symmetric authenticated encryption of plaintext using the secret;
// additionalData is used to protect un-encrypted parts of the message (header)
// in the authentication scope of the encryption.
export function encrypt(plain, secret, additionalData) {
    return __awaiter(this, void 0, void 0, function* () {
        const salt = crypto.getRandomValues(new Uint8Array(KDFSaltSize));
        const nonce = crypto.getRandomValues(new Uint8Array(AESGCMNonceSize));
        const key = yield hkdf(secret, salt);
        const encrypted = yield crypto.subtle.encrypt(aesGcmParams(nonce, additionalData), key, plain);
        return new Ciphertext({
            aes256GcmHkdfSha256: {
                payload: new Uint8Array(encrypted),
                hkdfSalt: salt,
                gcmNonce: nonce,
            },
        });
    });
}
// symmetric authenticated decryption of the encrypted ciphertext using the secret and additionalData
export function decrypt(encrypted, secret, additionalData) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!encrypted.aes256GcmHkdfSha256) {
            throw new Error('invalid payload ciphertext');
        }
        const key = yield hkdf(secret, encrypted.aes256GcmHkdfSha256.hkdfSalt);
        const decrypted = yield crypto.subtle.decrypt(aesGcmParams(encrypted.aes256GcmHkdfSha256.gcmNonce, additionalData), key, encrypted.aes256GcmHkdfSha256.payload);
        return new Uint8Array(decrypted);
    });
}
// helper for building Web Crypto API encryption parameter structure
function aesGcmParams(nonce, additionalData) {
    const spec = {
        name: 'AES-GCM',
        iv: nonce,
    };
    if (additionalData) {
        spec.additionalData = additionalData;
    }
    return spec;
}
// Derive AES-256-GCM key from a shared secret and salt.
// Returns crypto.CryptoKey suitable for the encrypt/decrypt API
function hkdf(secret, salt) {
    return __awaiter(this, void 0, void 0, function* () {
        const key = yield crypto.subtle.importKey('raw', secret, 'HKDF', false, [
            'deriveKey',
        ]);
        return crypto.subtle.deriveKey({ name: 'HKDF', hash: 'SHA-256', salt: salt, info: hkdfNoInfo }, key, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    });
}
//# sourceMappingURL=encryption.js.map